import { z } from 'zod';

export const productPaymentType = ['DEPRECIATION', 'IMMEDIATE'] as const;

export type ProductPaymentType = (typeof productPaymentType)[number];

export const productTaxAdvantage = [
  'BAD',
  'NEUTRAL',
  'GOOD',
  'VERY_GOOD',
  'EXCELLENT'
] as const;

export type ProductTaxAdvantage = (typeof productTaxAdvantage)[number];

export const budgetCode = [
  'END_OF_YEAR_PREMIUM',
  'CASH_BONUS',
  'SALES_COMMISSION',
  'EXTRA_HOLIDAYS',
  'MOBILITY_BUDGET',
  'LUMP_SUM'
] as const;

export type BudgetCode = (typeof budgetCode)[number];

export const budgetStatus = [
  'ACTIVE',
  'INACTIVE',
  'DRAFT',
  'ARCHIVED'
] as const;

export type BudgetStatus = (typeof budgetStatus)[number];

export const choiceStatus = [
  'PENDING',
  'CONFIRMED',
  'SELECTED',
  'SUBMITTED',
  'APPROVED',
  'CANCELED'
] as const;

export type ChoiceStatus = (typeof choiceStatus)[number];

export const transactionStatus = ['PENDING', 'CANCELED', 'CONFIRMED'] as const;

export type TransactionStatus = (typeof transactionStatus)[number];

export const userLanguage = ['en', 'fr', 'nl'] as const;

export type UserLanguage = (typeof userLanguage)[number];

export type UserEntityMetadataDomain = {
  firstname: FieldMetadata;
  lastname: FieldMetadata;
  email: FieldMetadata;
};

export type EmployeeEntityMetadataDomain = {
  payrollId: FieldMetadata;
  startDateEmployer: FieldMetadata;
};

export type FieldMetadataUserSource = {
  sourceType: 'user';
};

export type FieldMetadataIntegrationSource = {
  sourceType: 'integration';
  integrationId: string;
};

export type FieldMetadata =
  | FieldMetadataUserSource
  | FieldMetadataIntegrationSource;

export const claims = [
  'CHANGE_EMAIL_ADDRESS',
  'EDIT_FIRSTNAME',
  'EDIT_LASTNAME',
  'EDIT_WAGE_CONTRACTS',
  'ADD_USERS',
  'EDIT_PAYROLL_ID',
  'EDIT_START_DATE_EMPLOYER'
] as const;
export type Claim = (typeof claims)[number];

export const wageComponentCode = [
  'MONTHLY_BASE_SALARY',
  'BRUT_END_OF_YEAR_BONUS',
  'NET_END_OF_YEAR_BONUS',
  'LAPTOP',
  'LAPTOP_DEVICES',
  'GSM'
] as const;

export const companyReportingCode = [
  'BUDGET_SPENDING_REPORT',
  'EMPLOYEE_CREATION_AND_DEACTIVATION_DATES',
  'COOLBLUE_APPROVED_CHOICES',
  'EMPLOYEE_BUDGET_SPENT_BY_YEAR',
  'CHOICES_WITH_DEPRECIATION',
  'EMPLOYEES_WITH_NEGATIVE_BALANCE',
  'SPECIFIC_BUDGET_SPENDING_REPORT',
  'EXTRA_HOLIDAYS_REPORT',
  'BIKES_ANNUAL_REPORT'
] as const;

export type WageComponentCode = (typeof wageComponentCode)[number];

export type CompanyReportingCode = (typeof companyReportingCode)[number];

export const tenantReportingCode = [
  'TENANT_INVOICING_REPORT',
  'TENANT_EXTRA_HOLIDAYS_REPORT'
] as const;

export type TenantReportingCode = (typeof tenantReportingCode)[number];

export const currencyCode = ['EUR'] as const;

export type CurrencyCode = (typeof currencyCode)[number];

export const choiceDetailType = [
  'BOOLEAN',
  'DATE',
  'DATE_TIME',
  'FILE',
  'MONEY_AMOUNT',
  'NUMBER',
  'TEXT',
  'URL'
] as const;

export type ChoiceDetailType = (typeof choiceDetailType)[number];

export const orderStatus = ['PENDING', 'COMPLETED', 'CANCELED'] as const;

export type OrderStatus = (typeof orderStatus)[number];

export const wageContractType = ['MONTHLY_PAID', 'HOURLY_PAID'] as const;

export type WageContractType = (typeof wageContractType)[number];

export const wageDataType = [
  'GROSS_MONTHLY_WAGE',
  'WORKING_MINUTES_PER_WEEK',
  'EMPLOYEE_NSSO_COEFFICIENT',
  'EMPLOYER_NSSO_COEFFICIENT',
  'CHILDREN',
  'BRUT_END_OF_THE_YEAR',
  'NET_END_OF_THE_YEAR',
  'HOLIDAY_COST'
] as const;

export type WageDataType = (typeof wageDataType)[number];

export const recurrence = ['YEARLY', 'MONTHLY'] as const;

export type Recurrence = (typeof recurrence)[number];

export const companyProductStatus = [
  'ACTIVE',
  'INACTIVE',
  'EXPIRED',
  'DRAFT'
] as const;

export type CompanyProductStatus = (typeof companyProductStatus)[number];

export const companyProductCategories = [
  'CAR',
  'CASH',
  'ELECTRONICS',
  'HEALTH',
  'HOME_OFFICE',
  'HOUSING',
  'INTERNET',
  'MOBILITY',
  'SUBSCRIPTION',
  'UNCATEGORIZED',
  'WORK_LIFE_BALANCE'
] as const;

export type CompanyProductCategories =
  (typeof companyProductCategories)[number];

export const reccuringPaymentType = [
  'YEARLY_FIXED',
  'YEARLY_PRO_RATA',
  'DAILY_PRO_RATA'
] as const;

export type ReccuringPaymentType = (typeof reccuringPaymentType)[number];

export const immediatePaymentType = ['IMMEDIATE'] as const;

export const choicePaymentType = [
  ...reccuringPaymentType,
  ...immediatePaymentType
] as const;

export type ChoicePaymentType = (typeof choicePaymentType)[number];

export const tenants = ['CIPAL', 'LIANTIS', 'PAYFLIP2', 'ACERTA'] as const;

export type TenantType = (typeof tenants)[number];

export const getBudgetAllowedPaymentTypes = (
  budgetCode: BudgetCode,
  benefitPaymentType: ProductPaymentType
): ChoicePaymentType[] => {
  if (benefitPaymentType === 'IMMEDIATE') {
    return ['IMMEDIATE'];
  }
  switch (budgetCode) {
    case 'CASH_BONUS':
      return ['IMMEDIATE'];
    case 'SALES_COMMISSION':
      return ['IMMEDIATE'];
    case 'LUMP_SUM':
      return ['IMMEDIATE'];
    case 'END_OF_YEAR_PREMIUM':
      return ['IMMEDIATE', 'YEARLY_FIXED', 'YEARLY_PRO_RATA'];
    case 'EXTRA_HOLIDAYS':
      return ['IMMEDIATE', 'YEARLY_FIXED', 'YEARLY_PRO_RATA'];
    case 'MOBILITY_BUDGET':
      return ['IMMEDIATE', 'YEARLY_FIXED', 'YEARLY_PRO_RATA', 'DAILY_PRO_RATA'];
  }
};

export const companyDocumentType = ['FILE', 'URL'] as const;
export type CompanyDocumentType = (typeof companyDocumentType)[number];

export const signatureType = ['E_SIGN', 'MANUAL'] as const;
export type SignatureType = (typeof signatureType)[number];

export const EMPLOYEE_NSSO = 0.1307;

export const choiceExportFilter = ['orderDate', 'approvalDate'] as const;
export type ChoiceExportFilter = (typeof choiceExportFilter)[number];

export const cashOutTypes = [
  'MONTHLY_CASH_INCREASE',
  'EMPTY_BALANCE_CASH_OUT',
  'EARLY_CASH_OUT',
  'BUDGET_BALANCE_RESET',
  'REFRESHED_CASHOUT'
] as const;
export type CashOutType = (typeof cashOutTypes)[number];

export const payrollProviders = [
  'GENERIC',
  'ACERTA',
  'LIANTIS',
  'SD_WORX'
] as const;
export type PayrollProvider = (typeof payrollProviders)[number];

export const payrollLineRecurrence = ['MONTHLY', 'ONE-TIME'] as const;
export type PayrollLineRecurrence = (typeof payrollLineRecurrence)[number];

export const payrollLineEndDate = ['NONE', 'DEPRECIATION_PERIOD'] as const;
export type PayrollLineEndDate = (typeof payrollLineEndDate)[number];

export const payrollLineTiming = ['ON_APPROVAL', 'ON_DELIVERY'] as const;
export type PayrollLineTiming = (typeof payrollLineTiming)[number];

export const payrollLineType = [
  'BENEFIT_OF_ALL_KIND',
  'BENEFIT_OF_ALL_KIND_REST_VALUE',
  'PAY_OUT',
  'CASHOUT_PAY_OUT',
  'AMOUNT',
  'NOT_APPLICABLE'
] as const;
export type PayrollLineType = (typeof payrollLineType)[number];

export const budgetPayrollLineType = ['PAY_OUT', 'DEDUCTION'] as const;
export type BudgetPayrollLineType = (typeof budgetPayrollLineType)[number];

export const payrollFileType = ['MONTHLY_PRODUCTS'] as const;
export type PayrollFileType = (typeof payrollFileType)[number];

export const payrollRuleType = ['GROUP_SUM_VALUE'] as const;
export type PayrollRuleType = (typeof payrollRuleType)[number];

export const payrollExportType = ['EBLOX', 'DEFAULT'] as const;
export type PayrollExportType = (typeof payrollExportType)[number];

export const cashoutPaymentType = [
  'PAY_OUT',
  'DEDUCTION',
  'NO_IMPACT',
  'NO_BUDGET',
  'NOT_APPLICABLE'
] as const;
export type CashoutPaymentType = (typeof cashoutPaymentType)[number];

export const benefitType = ['SMARTPHONE', 'PC', 'TABLET'];
export type BenefitType = (typeof benefitType)[number];

export const exportFormats = ['XLSX', 'CSV'] as const;
export type ExportFormat = (typeof exportFormats)[number];

export const payrollInitializationGenerationTiming = [
  'IMMEDIATELY',
  'NEXT_PAYROLL_DATE'
] as const;
export type PayrollInitializationGenerationTiming =
  (typeof payrollInitializationGenerationTiming)[number];

export const eventKinds = [
  'BasketSubmittedEventDocument',
  'ChoiceRejectedEventDocument',
  'ChoiceApprovedEventDocument',
  'OrderCompletedEventDocument',
  'InviteEmployeeEventDocument',
  'BudgetTopUpEventDocument',
  'EmployeeBudgetCreatedEventDocument',
  'InviteAdminEventDocument',
  'AdminSignatureRequiredEventDocument',
  'NotifyAdminPayoutEventDocument',
  'EmptyBalanceCashOutFulfilledEventDocument',
  'ManualSignatureRejectedEventDocument',
  'BudgetActivationEventDocument'
] as const;
export type EventKind = (typeof eventKinds)[number];

export const productGroupsKind = [
  'OLEON_CAR',
  'PC',
  'PC_ACCESSORY',
  'TABLET_ACCESSORY',
  'SMARTWATCH',
  'DESK_CUPBOARD',
  'BIKE_LEASING',
  'BIKE_PURCHASE',
  'HEALTH_INSURANCE_CEILING',
  'BIKE_SWAP',
  'MOBILITY_HOUSING_COSTS',
  'GT_ALAN',
  'EXTRA_HOLIDAYS',
  'PENSION_SAVINGS',
  'KIDSLIFE_LIANTIS_REIMBURSEMENT_HOSPITALISATION_INSURANCE',
  'KMG_CHARGING_COSTS',
  'OLEON_HOLIDAYS',
  'HEALTH_INSURANCE_FREE_INPUT',
  'CAR_UPGRADE_AND_OPTIONS',
  'SMARTPHONE',
  'SMARTPHONE_ACCESSORY',
  'TABLET',
  'DESK_CHAIR',
  'DESK_TABLE',
  'DESK_LAMP',
  'DESK_MATERIAL_LIANTIS',
  'CHARGING_STATION',
  'HEALTH_INSURANCE',
  'HEALTH_INSURANCE_CIPAL',
  'HEALTH_INSURANCE_VAN_BREDA',
  'HEALTH_INSURANCE_ALAN',
  'MOBILITY_EXPENSES_RECURRING',
  'INTERNET',
  'BOFIDI_CAR_UPGRADE',
  'CHILDCARE_COSTS',
  'EVONIK_HOME_WORK_ALLOWANCE',
  'OLEON_MONTHLY_PREMIUM',
  'EDUCATION',
  'MOBILITY_EXPENSES_ONE_TIME',
  'WARRANTS',
  'FUEL_REIMBURSEMENT',
  'INSURANCE_REIMBURSEMENT'
] as const;
export type ProductGroupsKind = (typeof productGroupsKind)[number];

export const productStartDate = [
  'FORM_START_DATE',
  'DELIVERY_DATE',
  'APPROVAL_DATE'
] as const;
export type ProductStartDate = (typeof productStartDate)[number];

export const productEndDate = [
  'END_OF_RENTING_PERIOD',
  'NEXT_PAYMENT_DATE',
  'FORM_END_DATE',
  'END_YEAR_DATE'
] as const;
export type ProductEndDate = (typeof productEndDate)[number];

export const companyStatus = ['INACTIVE', 'ONGOING', 'DONE'] as const;
export type CompanyStatus = (typeof companyStatus)[number];

export const servicePackages = [
  'LEGACY',
  'LITE',
  'ESSENTIAL_STANDARD',
  'ESSENTIAL_STANDARD_SUPPORT',
  'ESSENTIAL_PREMIUM',
  'ESSENTIAL_PREMIUM_SUPPORT',
  'PRO_STANDARD',
  'PRO_STANDARD_SUPPORT',
  'PRO_PREMIUM',
  'PRO_PREMIUM_SUPPORT'
] as const;
export type ServicePackage = (typeof servicePackages)[number];

export function getCompanyReportByServicePackage(
  servicePackage: ServicePackage
): CompanyReportingCode[] {
  if (servicePackage === 'LEGACY' || servicePackage.includes('PRO')) {
    return [...companyReportingCode];
  }

  const allowedReports: CompanyReportingCode[] = ['BIKES_ANNUAL_REPORT'];

  if (servicePackage.includes('ESSENTIAL')) {
    allowedReports.push('BUDGET_SPENDING_REPORT');
  }

  return allowedReports;
}

export const checkoutStatuses = [
  'PENDING',
  'COMPLETED',
  'CANCELED',
  'EXPIRED'
] as const;
export type CheckoutStatus = (typeof checkoutStatuses)[number];

export const choiceSource = ['IMPORT', 'MANUAL', 'AUTO_RENEWAL'] as const;
export type ChoiceSource = (typeof choiceSource)[number];

export const productFieldTypes = z.enum([
  'TEXT',
  'LONG_TEXT',
  'CURRENCY',
  'DATE',
  'FILE',
  'URL'
]);

export type ProductFieldType = z.infer<typeof productFieldTypes>;

export const checkoutProductTypes = [
  'INSURANCE',
  'PC',
  'TABLET',
  'SMARTPHONE',
  'PC_ACCESSORY',
  'TABLET_ACCESSORY',
  'SMARTPHONE_ACCESSORY',
  'SMARTWATCH',
  'DESK_CHAIR',
  'DESK_TABLE'
] as const;

export type CheckoutProductType = (typeof checkoutProductTypes)[number];

export const PAYROLL_REGENERATION_TIME_LIMIT = 2; // value in months

export const integrationCategories = ['EMPLOYEE_MANAGEMENT'] as const;
export type IntegrationCategory = (typeof integrationCategories)[number];

export const companyIntegrationStatus = [
  'NOT_INSTALLED',
  'PENDING_INSTALLATION',
  'INSTALLED'
] as const;
export type CompanyIntegrationStatus =
  (typeof companyIntegrationStatus)[number];

export const renewableGenericInsuranceChoices = [
  'GENERAL_AMBULATORY_INSURANCE',
  'GENERAL_DENTAL',
  'GENERAL_DENTAL_AMBULATORY',
  'GENERAL_HOSPITALIZATION',
  'FREE_INPUT_HEALTH_INSURANCE',
  'GENERAL_HEALTH_INSURANCE',
  'ALAN_FOR_FAMILY_MEMBERS'
] as const;
export type RenewableGenericInsuranceChoice =
  (typeof renewableGenericInsuranceChoices)[number];

export const renewableInternetChoices = ['INTERNET'] as const;

export type RenewableInternetChoice = (typeof renewableInternetChoices)[number];

export const renewableSpecificInsuranceChoices = [
  'AG_HOSPITALIZATION',
  'AXA_HOSPITALIZATION',
  'DKV_AMBULATORY',
  'DKV_HOSPITALIZATION',
  'DVV_HOSPITALIZATION',
  'ETHIAS_HOSPITALIZATION',
  'KBC_HOSPITALIZATION'
] as const;
export type RenewableSpecificInsuranceChoice =
  (typeof renewableSpecificInsuranceChoices)[number];

export const SYSTEM_USER_ID = '000000000000000000000000'; // This is a valid ObjectId to be used as a placeholder for the system user

export const currencyScale = ['EUR/2'] as const;
export type CurrencyScale = (typeof currencyScale)[number];
